import type { ReactiveController, ReactiveControllerHost } from 'lit';
import {
  createActor,
  fromTransition,
  type ActorRefFromLogic,
  type Subscription,
} from 'xstate';

type ActorStateKey<State extends object> = Extract<keyof State, string>;

/**
 * A key/value-correlated event union.
 *
 * For `{ count: number; label: string }`, this becomes:
 *
 * - `{ type: 'state.set'; key: 'count'; value: number }`
 * - `{ type: 'state.set'; key: 'label'; value: string }`
 *
 * It is deliberately a mapped union rather than
 * `{ key: keyof State; value: State[keyof State] }`, which would allow a
 * string value to be paired with the numeric key.
 */
export type ActorStateUpdate<State extends object> = {
  [Key in ActorStateKey<State>]: {
    key: Key;
    value: State[Key];
  };
}[ActorStateKey<State>];

export type ActorStateSetEvent<State extends object> =
  ActorStateUpdate<State> & {
    type: 'state.set';
  };

export type ActorStatePatchEvent<State extends object> = {
  type: 'state.patch';
  updates: readonly ActorStateUpdate<State>[];
};

export type ActorStateEvent<State extends object> =
  | ActorStateSetEvent<State>
  | ActorStatePatchEvent<State>;

function createActorStateLogic<State extends object>(
  initialState: () => State,
) {
  return fromTransition(
    (
      state: Readonly<State>,
      event: ActorStateEvent<State>,
    ): Readonly<State> => {
      if (event.type === 'state.patch') {
        return event.updates.reduce<Readonly<State>>(
          (next, update) =>
            Object.is(next[update.key], update.value)
              ? next
              : { ...next, [update.key]: update.value },
          state,
        );
      }

      return Object.is(state[event.key], event.value)
        ? state
        : { ...state, [event.key]: event.value };
    },
    (): Readonly<State> => initialState(),
  );
}

type ActorStateLogic<State extends object> = ReturnType<
  typeof createActorStateLogic<State>
>;

type ActorStateRef<State extends object> = ActorRefFromLogic<
  ActorStateLogic<State>
>;

interface ActorStateHost extends ReactiveControllerHost {
  requestUpdate(name?: PropertyKey, oldValue?: unknown): void;
}

/**
 * Lit rendering bridge for one fully typed transition actor.
 *
 * `State` is a complete component/concern-specific context. Its event union is
 * generated from that context, so invalid keys, invalid values, and invalid
 * patches fail at compile time.
 */
export class ActorStateController<
  State extends object,
> implements ReactiveController {
  readonly actor: ActorStateRef<State>;

  private subscription: Subscription | undefined;

  constructor(
    private readonly host: ActorStateHost,
    logic: ActorStateLogic<State>,
  ) {
    this.actor = createActor(logic);
    this.actor.start();
    this.host.addController(this);
    this.subscribe();
  }

  hostConnected(): void {
    this.subscribe();
    // State may have changed while the element was detached and therefore had
    // no render subscription.
    this.host.requestUpdate();
  }

  hostDisconnected(): void {
    this.subscription?.unsubscribe();
    this.subscription = undefined;
  }

  get<Key extends ActorStateKey<State>>(key: Key): State[Key] {
    return this.actor.getSnapshot().context[key];
  }

  set<Key extends ActorStateKey<State>>(key: Key, value: State[Key]): void {
    const oldValue = this.get(key);
    if (Object.is(oldValue, value)) return;
    const event = {
      type: 'state.set' as const,
      key,
      value,
    } as ActorStateSetEvent<State>;
    this.actor.send(event);
    // Preserve Lit's changed-properties contract. Existing components use
    // `willUpdate/updated(changed)` for effects such as config-driven loading
    // and step-transition scrolling.
    this.host.requestUpdate(key, oldValue);
  }

  patch(...updates: readonly ActorStateUpdate<State>[]): void {
    const previous = this.actor.getSnapshot().context;
    this.actor.send({ type: 'state.patch', updates });
    for (const update of updates) {
      if (!Object.is(previous[update.key], update.value)) {
        this.host.requestUpdate(update.key, previous[update.key]);
      }
    }
  }

  private subscribe(): void {
    if (this.subscription) return;
    this.subscription = this.actor.subscribe(() => {
      this.host.requestUpdate();
    });
  }
}

/**
 * Owns one typed actor per host for a single component/concern state contract.
 *
 * Contracts are intentionally not global: the base configuration actor and a
 * schedule's discovery actor have different contexts and event unions. A
 * factory supplies a fresh, complete initial context for every element so
 * mutable arrays, records, Sets, and Dates never leak across instances.
 */
export class ActorStateContract<State extends object> {
  private readonly controllers = new WeakMap<
    ActorStateHost,
    ActorStateController<State>
  >();

  private readonly logic: ActorStateLogic<State>;

  constructor(initialState: () => State) {
    this.logic = createActorStateLogic(initialState);
  }

  controller(host: ActorStateHost): ActorStateController<State> {
    let controller = this.controllers.get(host);
    if (!controller) {
      controller = new ActorStateController(host, this.logic);
      this.controllers.set(host, controller);
    }
    return controller;
  }

  /**
   * Decorate a component field backed by this contract.
   *
   * Call sites must use the same state key in the decorator and field type:
   *
   * `@state.field('count') private count!: State['count'];`
   *
   * The key is compile-time checked here, the field value type is checked by
   * its indexed-access annotation, and the equality guard catches accidental
   * key/property drift at class-definition time.
   */
  field<Key extends ActorStateKey<State>>(key: Key): PropertyDecorator {
    // The closure preserves the contract while the descriptor's dynamic
    // `this` remains the component instance.
    const controllerFor = (host: ActorStateHost) => this.controller(host);

    return (prototype: object, propertyKey: string | symbol): void => {
      if (typeof propertyKey !== 'string') {
        throw new TypeError(
          'Actor state fields must use string property names',
        );
      }
      if (propertyKey !== key) {
        throw new TypeError(
          `Actor state key "${key}" must decorate property "${key}", not "${propertyKey}"`,
        );
      }

      Object.defineProperty(prototype, propertyKey, {
        configurable: true,
        enumerable: true,
        get(this: ActorStateHost): State[Key] {
          return controllerFor(this).get(key);
        },
        set(this: ActorStateHost, value: State[Key]): void {
          controllerFor(this).set(key, value);
        },
      });
    };
  }
}

export type ActorStateOf<Contract> =
  Contract extends ActorStateContract<infer State> ? State : never;

export function createActorState<State extends object>(
  initialState: () => State,
): ActorStateContract<State> {
  return new ActorStateContract(initialState);
}
